import { z } from 'zod';
import {
  AdminUserListQuerySchema,
  CajaUsuarioListQuerySchema,
  CajaUsuarioSchema,
  CreateAdminUserRequestSchema,
  CreateCajaUsuarioRequestSchema,
  CreateSucursalUsuarioRequestSchema,
  SetUserModuleRequestSchema,
  SucursalUsuarioListQuerySchema,
  SucursalUsuarioSchema,
  UpdateAdminUserRequestSchema,
} from '@visep/shared-types/admin';
import type {
  CajaUsuario,
  CajaUsuarioListQuery,
  CreateCajaUsuarioRequest,
  CreateSucursalUsuarioRequest,
  SetUserModuleRequest,
  SucursalUsuario,
  SucursalUsuarioListQuery,
} from '@visep/shared-types/admin';
import { PerfilSchema, UserModuleAssignmentSchema } from '@visep/shared-types/users';
import type { UserModuleAssignment } from '@visep/shared-types/users';
import { ModuleCodeSchema } from '@visep/shared-types/common';
import type { ModuleCode } from '@visep/shared-types/common';
import type { Helpers } from './internal.js';
import { makeCrudResource, toQuery } from './internal.js';

const ModulesResultSchema = z.object({
  items: z.array(UserModuleAssignmentSchema),
  // Teto do rol do usuário: módulos fora desta lista são descartados em silêncio
  // por getEnabledModules; a UI os anota/desabilita.
  allowedModules: z.array(ModuleCodeSchema).default([]),
});

export function createAdminResource({ request }: Helpers) {
  const users = makeCrudResource(request, {
    basePath: '/api/admin/users',
    schema: PerfilSchema,
    listQuerySchema: AdminUserListQuerySchema,
    createSchema: CreateAdminUserRequestSchema,
    updateSchema: UpdateAdminUserRequestSchema,
  });
  return {
    listUsers: users.list,
    getUser: users.get,
    createUser: users.create,
    updateUser: users.update,
    async listModules(
      userId: string,
    ): Promise<{ items: UserModuleAssignment[]; allowedModules: ModuleCode[] }> {
      return ModulesResultSchema.parse(await request('GET', `/api/admin/users/${encodeURIComponent(userId)}/modules`));
    },
    async setModule(userId: string, code: ModuleCode, input: SetUserModuleRequest): Promise<UserModuleAssignment> {
      return UserModuleAssignmentSchema.parse(
        await request('PUT', `/api/admin/users/${encodeURIComponent(userId)}/modules/${encodeURIComponent(code)}`, SetUserModuleRequestSchema.parse(input)),
      );
    },
    async listCajasUsuario(query?: CajaUsuarioListQuery): Promise<CajaUsuario[]> {
      const parsed = CajaUsuarioListQuerySchema.parse(query ?? {});
      const data = await request('GET', `/api/cajas-usuarios${toQuery(parsed)}`);
      return z.object({ items: z.array(CajaUsuarioSchema) }).parse(data).items;
    },
    async assignCaja(input: CreateCajaUsuarioRequest): Promise<CajaUsuario> {
      return CajaUsuarioSchema.parse(
        await request('POST', '/api/cajas-usuarios', CreateCajaUsuarioRequestSchema.parse(input)),
      );
    },
    async unassignCaja(id: string): Promise<void> {
      await request('DELETE', `/api/cajas-usuarios/${encodeURIComponent(id)}`);
    },
    async listSucursalesUsuario(query?: SucursalUsuarioListQuery): Promise<SucursalUsuario[]> {
      const parsed = SucursalUsuarioListQuerySchema.parse(query ?? {});
      const data = await request('GET', `/api/sucursales-usuarios${toQuery(parsed)}`);
      return z.object({ items: z.array(SucursalUsuarioSchema) }).parse(data).items;
    },
    async assignSucursal(input: CreateSucursalUsuarioRequest): Promise<SucursalUsuario> {
      return SucursalUsuarioSchema.parse(
        await request('POST', '/api/sucursales-usuarios', CreateSucursalUsuarioRequestSchema.parse(input)),
      );
    },
    async unassignSucursal(id: string): Promise<void> {
      await request('DELETE', `/api/sucursales-usuarios/${encodeURIComponent(id)}`);
    },
  };
}
